import { AnimatePresence } from 'motion/react';
import { useState } from 'react';

import { AuthBrand } from '../components/AuthBrand';
import { AuthSplitCopy } from '../components/AuthSplitCopy';
import { AuthSplitDecor } from '../components/AuthSplitDecor';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { ThemeToggle } from '../components/ThemeToggle';
import {
  RecoveryCodesReveal,
  TwoFactorEnrollModal,
} from '../components/TwoFactorEnrollModal';
import { useLanguage } from '../lib/i18n/LanguageContext';

/**
 * Forced TOTP enrollment for a self-service b2b org owner/admin — the
 * account type verify-email issues a session for specifically so this
 * screen exists to reach (see api.verifyEmail's requires_two_factor_setup
 * and LLMR-b2b-self-signup's first-login deadlock fix). This account type
 * has mandatory TOTP and zero enrolled factors; without a forced stop here,
 * the session verify-email issued would have full API access to everything
 * else in the app before 2FA is ever set up.
 *
 * Rendered in place of the whole signed-in route tree (see App.tsx's gate),
 * not as one route among Layout's children — a direct URL to any other page,
 * or a reload, must land back here too, not just the moment right after
 * verify-email. TOTP is the only method offered because b2b_admin is
 * TOTP-only per auth.AvailableMethods (llmr-shared/pkg/auth/policy.go) —
 * there is no method picker to build here.
 */
export function RequiredTwoFactorSetup({
  onComplete,
  onSignOut,
}: {
  /** Enrollment confirmed and the recovery codes have been acknowledged — safe to enter the app now. */
  onComplete: () => void;
  onSignOut: () => Promise<void>;
}) {
  const { dict } = useLanguage();
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);

  return (
    <div className="auth-screen auth-split">
      <div className="auth-theme-toggle">
        <LanguageSwitcher />
        <ThemeToggle />
      </div>

      <AuthBrand />

      <div className="auth-split-panel auth-split-panel--signup">
        <AuthSplitDecor />
        <AuthSplitCopy
          eyebrow={dict.twoFactorRequired.eyebrow}
          headline={dict.twoFactorRequired.title}
          body={dict.twoFactorRequired.body}
        />

        <div className="auth-split-form">
          <div className="auth-split-form-inner">
            <h1>{dict.twoFactorRequired.title}</h1>
            <p className="auth-lede">{dict.twoFactorRequired.body}</p>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {!recoveryCodes && (
          <TwoFactorEnrollModal
            method="totp"
            dismissible={false}
            cancelLabel={dict.twoFactorRequired.signOutInstead}
            onClose={() => void onSignOut()}
            onConfirmed={(codes) => setRecoveryCodes(codes)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {recoveryCodes && (
          <RecoveryCodesReveal codes={recoveryCodes} onDismiss={onComplete} />
        )}
      </AnimatePresence>
    </div>
  );
}
